/**
 * Custom hook for extracting and formatting metadata.
 * Processes event/asset data to provide formatted information for display,
 * including dates, genres, countries, duration, synopsis, and parental control handling.
 */
import Addon from '@shared/classes/addon';
import Content from '@shared/types/enums/content';
import DateUtils from '@shared/utils/date';
import Event from '@shared/classes/event';
import EventUtils from '@shared/utils/events';
import Focus from '@shared/types/enums/focus';
import FocusUtils from '@shared/utils/focus';
import MediaProduction from '@shared/classes/mediaProduction';
import Plan from '@shared/classes/plan';
import TranslationManager from '@shared/assets/locale/translationsManager';
import { ParentalAge } from '@shared/types/enums/parental';
import { categoriesSelector } from '@shared/store/slices/categories.slice';
import { selectFocus } from '@shared/store/slices/focus.slice';
import { genresSelector } from '@shared/store/slices/genres.slice';
import { useAppSelector } from '@shared/store/hooks';
import { useMemo } from 'react';
import { userSelector } from '@shared/store/slices/user.slice';

/**
 * Literals for the useMetadata hook.
 */
const LITERALS = 'PAGES.DETAIL';

/**
 * Props for the useMetadata hook.
 */
interface Props {
  asset?: Event | null;
  extended?: boolean;
  hasFilters?: boolean;
}

/**
 * Interface for the metadata hook return value.
 */
export interface AssetMetadata {
  ageCode?: ParentalAge;
  background?: string;
  catchup: boolean;
  channelLogo?: string | null;
  channelTitle?: string;
  commercialScoreText?: string;
  crew?: {
    actors?: { title: string; value: string };
    directors?: { title: string; value: string };
    writers?: { title: string; value: string };
  };
  date: string;
  displayAI: boolean;
  displaySeparator: boolean;
  displayTip: boolean;
  eventInfo?: string;
  eventTitle?: string;
  factsLine: string; // Year, category / genre, countries — pipe-separated
  info: string;
  isCurrentDate: boolean;
  isFutureDate: boolean;
  isPastDate: boolean;
  originalTitle?: { title: string; value: string };
  poster?: string;
  synopsis?: string;
  title?: string;
  upgradablePlan?: Plan | null;
}

/**
 * Custom hook that processes metadata and returns formatted information.
 * Handles date formatting, genre lookup, parental control checks, and extended information.
 *
 * @param {Event} asset - The event/asset to extract metadata from
 * @param {boolean} useExtendedInformation - Whether to include extended information (cast, directors, etc.)
 * @param {boolean} hasFilters - Whether filters are currently active (affects tip display)
 * @returns {object} Formatted metadata object
 */
export default function useMetadata({
  asset,
  extended = false,
  hasFilters = false,
}: Props): AssetMetadata {
  // Selectors
  const { data: categories } = useAppSelector(categoriesSelector);
  const { data: genres } = useAppSelector(genresSelector);
  const { parental } = useAppSelector(userSelector);
  const focus = useAppSelector(selectFocus);

  // Memoized asset info
  const metadata = useMemo(() => {
    // Destructure asset
    const {
      ageCode,
      channelLogo,
      score: commercialScore,
      commercialScoreText,
      upgradablePlan,
    } = asset || {};

    // Get attributes from asset
    const category = categories?.find(
      (item) => item.id === asset?.categoryId
    )?.title;
    const channelTitle = asset?.channel?.title;
    const countries = asset?.getCountries && asset?.getCountries();
    const duration = (asset as MediaProduction)?.duration
      ? `${(asset as MediaProduction)?.duration}min`
      : '';
    const genre = genres?.find(
      (item) => item.id === asset?.genreId || item.name === asset?.genreId
    )?.title;
    const parentalControl =
      (asset as Addon)?.parentalControl || asset?.channel?.parentalControl;
    const poster = asset?.poster || asset?.photo;
    const synopsis =
      parental?.enabled && parentalControl
        ? TranslationManager.get('MISC.MORE')
        : asset?.synopsis;
    const title = asset?.title;
    const year = asset?.year ?? '';

    // Join category and genre
    const categoryAndGenre =
      category && genre ? `${category} / ${genre}` : category || genre || '';

    // Create info text and separate content
    const info = [year, categoryAndGenre, countries, duration]
      .filter((item) => !!item)
      .join(' · ');

    // Detail page: year | category / genre | countries (channel is shown separately)
    const factsLine = [year, categoryAndGenre, countries]
      .filter((item) => !!item)
      .join(' | ');

    // Calculate date & catchup
    const catchup = asset?.catchup ?? false;
    let date = '';
    const isCurrentDate = DateUtils.isCurrentDate(
      asset?.date?.start,
      asset?.date?.end
    );
    const isPastDate = DateUtils.isPastDate(asset?.date?.end);
    const isFutureDate = DateUtils.isFutureDate(asset?.date?.start);

    // Date display logic
    const displayBeginAndEndDate =
      (asset?.type === Content.EPG_EVENT && !isPastDate) ||
      asset?.type === Content.MEDIA_PRODUCTION;

    // Display begin and end date
    if (displayBeginAndEndDate) {
      const day = DateUtils.getTime({
        customDate: asset?.date?.start,
        displaySeconds: false,
        displayMinutes: false,
        displayHours: false,
        displayDayOfWeek: true,
        displayDay: true,
        displayMonth: true,
        displayYear: true,
        trimMonth: true,
        trimDayOfTheWeek: true,
        separator: ' ',
      });
      const startTime = `${day} | ${TranslationManager.get(
        'PAGES.DETAIL.DATE.BEGIN'
      )} ${DateUtils.getTime({
        customDate: asset?.date?.start,
        displaySeconds: false,
      })}`;
      const endTime = `${TranslationManager.get(
        'PAGES.DETAIL.DATE.END'
      )} ${DateUtils.getTime({
        customDate: asset?.date?.end,
        displaySeconds: false,
      })}`;
      date = [startTime, endTime].filter((item) => !!item).join(' | ');
    }

    // Display conditions
    const displayAI = asset?.type === Content.IA_CORNER;
    const displaySeparator = !!commercialScore;
    const displayTip =
      (asset &&
        asset.type !== Content.ADDON &&
        asset.type !== Content.CORNER &&
        asset.type !== Content.IA_CORNER &&
        (!hasFilters ||
          (hasFilters && // Check if focus is in filters or logo to display tip
            !FocusUtils.evaluateFocus(parseInt(Focus.RAILS_SUB_LIST + '0')) &&
            !FocusUtils.evaluateFocus(Focus.CHANNEL_GUIDE_LOGO)))) ??
      false;

    // ------------------------------------------------------------
    // Return asset info
    // ------------------------------------------------------------

    return {
      ageCode,
      catchup,
      channelLogo,
      channelTitle,
      commercialScoreText,
      date,
      displayAI,
      displaySeparator,
      displayTip,
      factsLine,
      info,
      isCurrentDate,
      isFutureDate,
      isPastDate,
      poster,
      synopsis,
      title,
      upgradablePlan,
      ...(extended && {
        background: asset?.background || '',
        crew: {
          actors: {
            title: TranslationManager.get(`${LITERALS}.SECTIONS.CAST`),
            value: EventUtils.getCrew('actors', asset || null),
          },
          directors: {
            title: TranslationManager.get(`${LITERALS}.SECTIONS.DIRECTOR`),
            value: EventUtils.getCrew('directors', asset || null),
          },
          writers: {
            title: TranslationManager.get(`${LITERALS}.SECTIONS.SCRIPT`),
            value: EventUtils.getCrew('writers', asset || null),
          },
        },
        eventInfo: EventUtils.getInfo(asset, true) || '',
        eventTitle: asset?.title,
        originalTitle: {
          title: TranslationManager.get(`${LITERALS}.SECTIONS.OG_TITLE`),
          value: asset?.episodeTitle || asset?.title || '',
        },
      }),
    };
  }, [asset, extended, hasFilters, focus, parental]);

  // Return metadata
  return metadata;
}
